import { useState, useEffect, useRef } from 'react';
import '../styles/AdminCategorias.css';
import '../styles/EditorProducto.css';

const AdminCategorias = ({ isOpen, onClose }) => {
  const [categorias, setCategorias]       = useState([]);
  const [loadingCats, setLoadingCats]     = useState(true);
  const [nuevaNombre, setNuevaNombre]     = useState('');
  const [editandoId, setEditandoId]       = useState(null);
  const [editandoNombre, setEditandoNombre] = useState('');
  const [guardando, setGuardando]         = useState(false);
  const [uploadingId, setUploadingId]     = useState(null); // id de la cat cuya imagen está subiendo
  const [error, setError]                 = useState('');
  const [exito, setExito]                 = useState('');
  // Subcategoría en creación: { parentId }
  const [creandoSubId, setCreandoSubId]   = useState(null);
  const [nuevaSubNombre, setNuevaSubNombre] = useState('');

  // Refs para file inputs (uno por categoría padre)
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (!isOpen) return;
    setLoadingCats(true);
    setError('');
    setExito('');
    fetch('/api/categorias')
      .then(res => res.json())
      .then(data => setCategorias(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudieron cargar las categorías'))
      .finally(() => setLoadingCats(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const mostrarExito = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(''), 2500);
  };

  const padres = categorias
    .filter(c => !c.parent_id)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const hijosde = (parentId) =>
    categorias
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const handleCrear = async (e) => {
    e.preventDefault();
    const nombre = nuevaNombre.trim();
    if (!nombre) return;
    setGuardando(true);
    setError('');
    try {
      const res = await fetch('/api/categorias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al crear la categoría');
      setCategorias(prev => [...prev, data.categoria]);
      setNuevaNombre('');
      mostrarExito(`Categoría "${nombre}" creada`);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleCrearSub = async (e, parentId) => {
    e.preventDefault();
    const nombre = nuevaSubNombre.trim();
    if (!nombre) return;
    setGuardando(true);
    setError('');
    try {
      const res = await fetch('/api/categorias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, parent_id: parentId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al crear la subcategoría');
      setCategorias(prev => [...prev, data.categoria]);
      setNuevaSubNombre('');
      setCreandoSubId(null);
      mostrarExito(`Subcategoría "${nombre}" creada`);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const iniciarEdicion = (cat) => {
    setEditandoId(cat._id);
    setEditandoNombre(cat.nombre);
    setCreandoSubId(null);
    setError('');
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setEditandoNombre('');
  };

  const handleRenombrar = async (e, id) => {
    e.preventDefault();
    const nombre = editandoNombre.trim();
    if (!nombre) return;
    setGuardando(true);
    setError('');
    try {
      const res = await fetch(`/api/categorias/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al renombrar');
      setCategorias(prev =>
        prev.map(c => c._id === id ? { ...c, nombre: data.categoria.nombre } : c)
      );
      cancelarEdicion();
      mostrarExito('Categoría renombrada');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id, nombre, esPadre) => {
    const msg = esPadre
      ? `¿Eliminar la categoría "${nombre}" y todas sus subcategorías?`
      : `¿Eliminar la subcategoría "${nombre}"?`;
    if (!window.confirm(msg)) return;
    setGuardando(true);
    setError('');
    try {
      const res = await fetch(`/api/categorias/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al eliminar');
      setCategorias(prev => prev.filter(c => c._id !== id && c.parent_id !== id));
      mostrarExito(`"${nombre}" eliminada`);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleSubirImagen = async (e, catId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(catId);
    setError('');
    try {
      const fd = new FormData();
      fd.append('imagenes', file);
      const uploadRes = await fetch('/api/productos/upload', { method: 'POST', body: fd });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(uploadData.error || 'Error al subir imagen');
      const url = uploadData.urls?.[0];
      if (!url) throw new Error('No se obtuvo URL de la imagen');

      const patchRes = await fetch(`/api/categorias/${catId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imagen: url }),
      });
      const patchData = await patchRes.json();
      if (!patchRes.ok) throw new Error(patchData.error || 'Error al guardar imagen');

      setCategorias(prev =>
        prev.map(c => c._id === catId ? { ...c, imagen: url } : c)
      );
      mostrarExito('Imagen actualizada');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingId(null);
      // Reset file input
      if (fileInputRefs.current[catId]) fileInputRefs.current[catId].value = '';
    }
  };

  const handleQuitarImagen = async (catId) => {
    setGuardando(true);
    setError('');
    try {
      const res = await fetch(`/api/categorias/${catId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imagen: '' }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Error al quitar imagen');
      }
      setCategorias(prev =>
        prev.map(c => c._id === catId ? { ...c, imagen: '' } : c)
      );
      mostrarExito('Imagen quitada');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const renderItem = (cat, esSub = false) => {
    if (editandoId === cat._id) {
      return (
        <li key={cat._id} className={`cat-item${esSub ? ' cat-item-sub' : ''}`}>
          <form className="cat-edit-form" onSubmit={e => handleRenombrar(e, cat._id)}>
            <input
              type="text"
              value={editandoNombre}
              onChange={e => setEditandoNombre(e.target.value)}
              autoFocus
              disabled={guardando}
              maxLength={60}
            />
            <button type="submit" disabled={guardando || !editandoNombre.trim()} title="Guardar">✓</button>
            <button type="button" onClick={cancelarEdicion} disabled={guardando} title="Cancelar">✕</button>
          </form>
        </li>
      );
    }
    return (
      <li key={cat._id} className={`cat-item${esSub ? ' cat-item-sub' : ''}`}>
        <span className="cat-nombre">{esSub && <span className="cat-sub-prefix">↳ </span>}{cat.nombre}</span>
        <div className="cat-acciones">
          <button onClick={() => iniciarEdicion(cat)} title="Renombrar" disabled={guardando}>✎</button>
          <button
            onClick={() => handleEliminar(cat._id, cat.nombre, !esSub)}
            title="Eliminar"
            className="cat-btn-eliminar"
            disabled={guardando}
          >🗑</button>
        </div>
      </li>
    );
  };

  return (
    <div className="cat-modal-overlay" onClick={onClose}>
      <div className="cat-modal" onClick={e => e.stopPropagation()}>

        <div className="cat-header">
          <h2>🏷️ Categorías</h2>
          <button className="cat-close-btn" onClick={onClose} title="Cerrar">✕</button>
        </div>

        <div className="cat-body">
          {/* Agregar nueva categoría raíz */}
          <form className="cat-add-form" onSubmit={handleCrear}>
            <input
              type="text"
              value={nuevaNombre}
              onChange={e => { setNuevaNombre(e.target.value); setError(''); }}
              placeholder="Nueva categoría principal..."
              disabled={guardando}
              maxLength={60}
            />
            <button
              type="submit"
              className="cat-add-btn"
              disabled={guardando || !nuevaNombre.trim()}
              title="Agregar"
            >
              {guardando ? <span className="spinner spinner-dark" /> : '+'}
            </button>
          </form>

          {error && <div className="form-message form-error">⚠️ {error}</div>}
          {exito && <div className="form-message form-success">✓ {exito}</div>}

          {loadingCats ? (
            <div className="cat-loading">Cargando categorías...</div>
          ) : categorias.length === 0 ? (
            <div className="cat-empty">No hay categorías aún. ¡Creá la primera!</div>
          ) : (
            <ul className="cat-list">
              {padres.map(padre => {
                const subs = hijosde(padre._id);
                const subiendo = uploadingId === padre._id;
                return (
                  <li key={padre._id} className="cat-group">
                    {/* Fila del padre */}
                    {editandoId === padre._id ? (
                      <div className="cat-item">
                        <form className="cat-edit-form" onSubmit={e => handleRenombrar(e, padre._id)}>
                          <input
                            type="text"
                            value={editandoNombre}
                            onChange={e => setEditandoNombre(e.target.value)}
                            autoFocus
                            disabled={guardando}
                            maxLength={60}
                          />
                          <button type="submit" disabled={guardando || !editandoNombre.trim()} title="Guardar">✓</button>
                          <button type="button" onClick={cancelarEdicion} disabled={guardando} title="Cancelar">✕</button>
                        </form>
                      </div>
                    ) : (
                      <div className="cat-item cat-item-padre">
                        {/* Thumbnail de imagen */}
                        <div className="cat-img-wrap">
                          {padre.imagen ? (
                            <>
                              <img src={padre.imagen} alt={padre.nombre} className="cat-img-thumb" />
                              <button
                                className="cat-img-quitar"
                                onClick={() => handleQuitarImagen(padre._id)}
                                title="Quitar imagen"
                                disabled={guardando || subiendo}
                              >✕</button>
                            </>
                          ) : (
                            <div className="cat-img-empty" title="Sin imagen">📷</div>
                          )}
                        </div>

                        <span className="cat-nombre">{padre.nombre}</span>

                        <div className="cat-acciones">
                          {/* Botón subir imagen */}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            ref={el => { fileInputRefs.current[padre._id] = el; }}
                            onChange={e => handleSubirImagen(e, padre._id)}
                          />
                          <button
                            onClick={() => fileInputRefs.current[padre._id]?.click()}
                            title={padre.imagen ? 'Cambiar imagen' : 'Subir imagen'}
                            disabled={guardando || subiendo}
                            className="cat-btn-foto"
                          >
                            {subiendo ? <span className="spinner spinner-dark" /> : '🖼'}
                          </button>
                          <button onClick={() => iniciarEdicion(padre)} title="Renombrar" disabled={guardando || subiendo}>✎</button>
                          <button
                            onClick={() => handleEliminar(padre._id, padre.nombre, true)}
                            title="Eliminar"
                            className="cat-btn-eliminar"
                            disabled={guardando || subiendo}
                          >🗑</button>
                        </div>
                      </div>
                    )}

                    {/* Subcategorías */}
                    {subs.length > 0 && (
                      <ul className="cat-list-sub">
                        {subs.map(sub => renderItem(sub, true))}
                      </ul>
                    )}

                    {/* Formulario inline para nueva subcategoría */}
                    {creandoSubId === padre._id ? (
                      <form className="cat-add-sub-form" onSubmit={e => handleCrearSub(e, padre._id)}>
                        <span className="cat-sub-prefix">↳ </span>
                        <input
                          type="text"
                          value={nuevaSubNombre}
                          onChange={e => { setNuevaSubNombre(e.target.value); setError(''); }}
                          placeholder="Nueva subcategoría..."
                          disabled={guardando}
                          maxLength={60}
                          autoFocus
                        />
                        <button type="submit" disabled={guardando || !nuevaSubNombre.trim()} title="Agregar">✓</button>
                        <button type="button" onClick={() => { setCreandoSubId(null); setNuevaSubNombre(''); }} disabled={guardando} title="Cancelar">✕</button>
                      </form>
                    ) : (
                      <button
                        className="cat-btn-add-sub"
                        onClick={() => { setCreandoSubId(padre._id); setNuevaSubNombre(''); setEditandoId(null); }}
                        disabled={guardando}
                      >
                        + Subcategoría
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminCategorias;
